import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  LuArrowLeft,
  LuBriefcaseBusiness,
  LuClipboardList,
  LuDownload,
  LuEllipsisVertical,
  LuFileText,
  LuInfo,
  LuLayers,
  LuLock,
  LuPaperclip,
  LuSend,
  LuShield,
  LuStar,
  LuUserPlus,
  LuUserRound,
  LuX,
} from "react-icons/lu";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import {
  FEEDBACK_STATUS_OPTIONS,
  assignManagerFeedback,
  feedbackExtractMessage,
  fetchManagerFeedbackAssignees,
  fetchManagerFeedbackById,
  fetchSuperAdminFeedbackById,
  fetchSupportFeedbackById,
  postManagerFeedbackNote,
  postSupportFeedbackMessage,
  updateManagerFeedbackStatus,
  updateSupportFeedbackStatus,
} from "../../services/platformFeedback.service";
import "../../assets/styles/support_disputes.scss";

function formatDateTime(value) {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleString();
}

function formatStatus(status, label) {
  if (label) return label;
  return String(status || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function isImageAttachment(mime, name = "") {
  if (String(mime || "").toLowerCase().startsWith("image/")) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(String(name || ""));
}

function senderLabel(type) {
  if (type === "support" || type === "support_manager" || type === "manager") return "Support";
  if (type === "customer") return "Customer";
  if (type === "system") return "System";
  return "User";
}

function renderRatingStars(rating) {
  const value = Math.max(0, Math.min(5, Math.trunc(Number(rating) || 0)));
  return (
    <span className="sd-rating-stars" title={`${value} / 5`} aria-label={`Rating ${value} out of 5`}>
      {Array.from({ length: 5 }, (_, index) => (
        <LuStar key={index} aria-hidden className={index < value ? "is-filled" : ""} />
      ))}
      <span className="sd-rating-stars__value">{value} / 5</span>
    </span>
  );
}

function resolveFeedbackFetch(mode) {
  if (mode === "manager") return fetchManagerFeedbackById;
  if (mode === "super-admin") return fetchSuperAdminFeedbackById;
  return fetchSupportFeedbackById;
}

function resolvePermissions(mode) {
  if (mode === "support") {
    return {
      canUpdateStatus: true,
      canAddInternalNotes: true,
      canAssign: false,
      statusRestrictionMessage: null,
    };
  }
  if (mode === "manager") {
    return {
      canUpdateStatus: true,
      canAddInternalNotes: true,
      canAssign: true,
      statusRestrictionMessage: null,
    };
  }
  return {
    canUpdateStatus: false,
    canAddInternalNotes: false,
    canAssign: false,
    statusRestrictionMessage: "Super Admin has read-only feedback oversight.",
  };
}

export default function SupportFeedbackDetailPage({
  mode = "support",
  backPath = "/dashboard/support/feedback",
  backLabel = "My Assigned Feedback",
}) {
  const params = useParams();
  const feedbackId = params.id || params.feedbackId;
  const navigate = useNavigate();
  const chatEndRef = useRef(null);
  const fileInputRef = useRef(null);
  const messageInputRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [status, setStatus] = useState("");
  const [resolutionNotes, setResolutionNotes] = useState("");
  const [savingStatus, setSavingStatus] = useState(false);
  const [message, setMessage] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [sending, setSending] = useState(false);
  const [showAllTimeline, setShowAllTimeline] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignees, setAssignees] = useState([]);
  const [assigneeId, setAssigneeId] = useState("");
  const [assignReason, setAssignReason] = useState("");
  const [assignNotes, setAssignNotes] = useState("");
  const [assigning, setAssigning] = useState(false);

  const fetchDetail = resolveFeedbackFetch(mode);
  const permissions = resolvePermissions(mode);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchDetail(feedbackId);
      if (!data?.feedback) throw new Error("Feedback not found.");
      setDetail(data);
      setStatus(data.feedback.status);
      setResolutionNotes(data.feedback.resolutionNotes || "");
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to load feedback."));
      navigate(backPath);
    } finally {
      setLoading(false);
    }
  }, [backPath, fetchDetail, feedbackId, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [detail?.messages]);

  useEffect(() => {
    if (mode === "support") return undefined;
    const timer = setInterval(() => {
      if (document.visibilityState === "visible") {
        fetchDetail(feedbackId)
          .then((data) => {
            if (data?.feedback) setDetail(data);
          })
          .catch(() => {});
      }
    }, 15000);
    return () => clearInterval(timer);
  }, [fetchDetail, feedbackId, mode]);

  const onSaveStatus = async () => {
    if (!permissions.canUpdateStatus) {
      toast.error(permissions.statusRestrictionMessage || "You cannot update this feedback status.");
      return;
    }
    if (!status) {
      toast.error("Please select a status.");
      return;
    }
    setSavingStatus(true);
    try {
      const payload = {
        status,
        resolutionNotes:
          status === "resolved" || status === "closed" ? resolutionNotes : undefined,
      };
      const data =
        mode === "manager"
          ? await updateManagerFeedbackStatus(feedbackId, payload)
          : await updateSupportFeedbackStatus(feedbackId, payload);
      setDetail(data);
      toast.success("Status updated.");
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to update status."));
    } finally {
      setSavingStatus(false);
    }
  };

  const onSendMessage = async (event) => {
    event.preventDefault();
    if (!permissions.canAddInternalNotes) return;
    if (!message.trim() && !attachment) {
      toast.error("Enter a note or attach a file.");
      return;
    }
    setSending(true);
    try {
      const data =
        mode === "manager"
          ? await postManagerFeedbackNote(feedbackId, {
              message: message.trim(),
              attachment,
            })
          : await postSupportFeedbackMessage(feedbackId, {
              message: message.trim(),
              isInternal: true,
              attachment,
            });
      setDetail(data);
      setMessage("");
      setAttachment(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      toast.success("Internal note added.");
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to add note."));
    } finally {
      setSending(false);
    }
  };

  const beginInternalNote = () => {
    if (!permissions.canAddInternalNotes) {
      toast.info(permissions.statusRestrictionMessage || "Conversation is read-only.");
      return;
    }
    messageInputRef.current?.focus();
  };

  const openAssignModal = async () => {
    if (!permissions.canAssign) return;
    setShowAssignModal(true);
    setAssigneeId(detail?.feedback?.assignedToAccessUserId || "");
    setAssignReason("");
    setAssignNotes("");
    try {
      const list = await fetchManagerFeedbackAssignees();
      setAssignees(Array.isArray(list) ? list : []);
    } catch {
      setAssignees([]);
    }
  };

  const submitAssign = async () => {
    if (!assigneeId) {
      toast.error("Select a support user.");
      return;
    }
    setAssigning(true);
    try {
      await assignManagerFeedback(feedbackId, {
        accessUserId: Number(assigneeId),
        reason: assignReason.trim() || undefined,
        notes: assignNotes.trim() || undefined,
      });
      toast.success("Feedback assigned.");
      setShowAssignModal(false);
      await load();
    } catch (error) {
      toast.error(feedbackExtractMessage(error, "Failed to assign feedback."));
    } finally {
      setAssigning(false);
    }
  };

  if (loading || !detail?.feedback) {
    return (
      <div className="sd-page">
        <p className="sd-empty">Loading feedback details...</p>
      </div>
    );
  }

  const feedback = detail.feedback;
  const messages = Array.isArray(detail.messages) ? detail.messages : [];
  const activities = Array.isArray(detail.activities) ? detail.activities : [];
  const visibleTimeline = showAllTimeline ? activities : activities.slice(0, 3);
  const feedbackAttachments = Array.isArray(detail.attachments) ? detail.attachments : [];
  const attachments = [
    ...feedbackAttachments.map((file) => ({
      id: `att-${file.id}`,
      name: file.fileName || "Attachment",
      path: file.filePath,
    })),
    ...messages
      .filter((item) => item.attachmentPath)
      .map((item) => ({
        id: `message-${item.id}`,
        name: item.attachmentName || "Message attachment",
        path: item.attachmentPath,
      })),
  ];
  const customerPhone = [feedback.customerCountryCode, feedback.customerPhone]
    .filter(Boolean)
    .join(" ")
    .trim();
  const rating = Math.max(0, Math.min(5, Math.trunc(Number(feedback.rating) || 0)));
  const statusChoices = FEEDBACK_STATUS_OPTIONS.filter((opt) => opt.value);

  return (
    <div className="sd-page sd-detail-page">
      <nav className="sd-breadcrumb" aria-label="Breadcrumb">
        <Link to={backPath}>{backLabel}</Link>
        <span aria-hidden>&gt;</span>
        <span aria-current="page">{feedback.feedbackNumber}</span>
      </nav>

      <header className="sd-detail-head">
        <div className="sd-detail-title-row">
          <button
            type="button"
            className="sd-icon-btn sd-icon-btn--ghost"
            onClick={() => navigate(backPath)}
            aria-label="Back"
          >
            <LuArrowLeft />
          </button>
          <div>
            <h1>{feedback.feedbackNumber}</h1>
            <p>{feedback.subject}</p>
          </div>
        </div>
        <div className="sd-detail-badges">
          <span className={`sd-pill sd-pill-status sd-pill-status--${feedback.status}`}>
            {formatStatus(feedback.status, feedback.statusLabel)}
          </span>
          <span className="sd-pill">{feedback.feedbackType || "—"}</span>
          <span className="sd-pill sd-pill--rating">{renderRatingStars(rating)}</span>
          <button type="button" className="sd-icon-btn sd-icon-btn--ghost" aria-label="More actions">
            <LuEllipsisVertical />
          </button>
        </div>
      </header>

      <div className="sd-detail-shell">
        <main className="sd-detail-main">
          <section className="sd-party-grid">
            <article className="sd-card sd-info-card">
              <h2>
                <LuClipboardList aria-hidden /> Feedback Summary
              </h2>
              <dl>
                <dt>Type</dt>
                <dd>{feedback.feedbackType || "—"}</dd>
                <dt>Area</dt>
                <dd>{feedback.areaLabel || "—"}</dd>
                <dt>Rating</dt>
                <dd>{renderRatingStars(rating)}</dd>
                <dt>Submitted On</dt>
                <dd>{formatDateTime(feedback.createdAt)}</dd>
                <dt>Last Updated</dt>
                <dd>{formatDateTime(feedback.updatedAt)}</dd>
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2>
                <LuUserRound aria-hidden /> Customer Information
              </h2>
              <dl>
                <dt>Name</dt>
                <dd>{feedback.customerName || "—"}</dd>
                <dt>Email</dt>
                <dd>{feedback.customerEmail || "—"}</dd>
                <dt>Phone</dt>
                <dd>{customerPhone || "—"}</dd>
              </dl>
            </article>
          </section>

          <section className="sd-party-grid">
            <article className="sd-card sd-info-card">
              <h2>
                <LuBriefcaseBusiness aria-hidden /> Assignment Information
              </h2>
              <dl>
                <dt>Assigned To</dt>
                <dd>{feedback.assignedToName || "—"}</dd>
                <dt>Assignment Status</dt>
                <dd>
                  {feedback.assignmentStatus === "assigned" ? "Assigned" : "Unassigned"}
                </dd>
                <dt>Assigned At</dt>
                <dd>{formatDateTime(feedback.assignedAt)}</dd>
                <dt>Manager</dt>
                <dd>{feedback.managerName || "—"}</dd>
                <dt>Assignee Email</dt>
                <dd>{feedback.assignedToEmail || "—"}</dd>
                {feedback.assignmentReason ? (
                  <>
                    <dt>Assignment Reason</dt>
                    <dd>{feedback.assignmentReason}</dd>
                  </>
                ) : null}
              </dl>
            </article>
            <article className="sd-card sd-info-card">
              <h2>
                <LuLayers aria-hidden /> Resolution
              </h2>
              <dl>
                <dt>Resolved At</dt>
                <dd>{formatDateTime(feedback.resolvedAt)}</dd>
                <dt>Closed At</dt>
                <dd>{formatDateTime(feedback.closedAt)}</dd>
                <dt>Resolution Notes</dt>
                <dd>{feedback.resolutionNotes || "—"}</dd>
              </dl>
            </article>
          </section>

          <section className="sd-card sd-info-card sd-description-card">
            <h2>
              <LuFileText aria-hidden /> Full Description
            </h2>
            <div className="sd-description-body">
              {feedback.description?.trim()
                ? feedback.description.trim()
                : "No description provided."}
            </div>
          </section>

          <section className="sd-card sd-conversation">
            <header className="sd-conversation__head">
              <div>
                Internal Notes &amp; Messages
                <small>
                  {permissions.canAddInternalNotes
                    ? "Internal notes are hidden from the customer."
                    : "View the complete note history."}
                </small>
              </div>
              {permissions.canAddInternalNotes ? (
                <span className="sd-internal-only">
                  <LuLock aria-hidden /> Internal only
                </span>
              ) : (
                <span className="sd-internal-only">
                  <LuLock aria-hidden /> Read-only
                </span>
              )}
            </header>
            <div className={`sd-chat${messages.length === 0 ? " sd-chat--empty" : ""}`}>
              <div className="sd-chat__thread" aria-live="polite">
                {messages.length === 0 ? (
                  <p className="sd-empty">No messages yet.</p>
                ) : (
                  messages.map((msg) => (
                    <div
                      key={msg.id}
                      className={`sd-chat__bubble sd-chat__bubble--${
                        msg.senderType === "customer" ? "buyer" : msg.senderType || "system"
                      }${msg.isInternal ? " is-internal" : ""}`}
                    >
                      <header>
                        <strong>
                          {msg.senderName || "User"}{" "}
                          <span>({senderLabel(msg.senderType)})</span>
                        </strong>
                        {msg.isInternal ? (
                          <em>
                            <LuLock aria-hidden /> Internal
                          </em>
                        ) : null}
                        <time>{formatDateTime(msg.createdAt)}</time>
                      </header>
                      <p>{msg.messageBody}</p>
                      {msg.attachmentPath ? (
                        isImageAttachment(null, msg.attachmentName) ? (
                          <a
                            className="sd-chat__image"
                            href={resolvePublicFileUrl(msg.attachmentPath)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img
                              src={resolvePublicFileUrl(msg.attachmentPath)}
                              alt={msg.attachmentName || "Conversation attachment"}
                            />
                          </a>
                        ) : (
                          <a
                            className="sd-chat__file"
                            href={resolvePublicFileUrl(msg.attachmentPath)}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <LuPaperclip aria-hidden /> {msg.attachmentName || "Attachment"}
                          </a>
                        )
                      ) : null}
                    </div>
                  ))
                )}
                <div ref={chatEndRef} />
              </div>

              {permissions.canAddInternalNotes ? (
                <form className="sd-chat__composer" onSubmit={onSendMessage}>
                  <div className="sd-internal-banner">
                    <LuShield aria-hidden /> Internal note — hidden from customer
                  </div>
                  <textarea
                    ref={messageInputRef}
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Write an internal note for support / admin..."
                    rows={3}
                  />
                  <div className="sd-chat__composer-actions">
                    <label className="sd-attach-action">
                      <LuPaperclip aria-hidden /> Attach File
                      <input
                        ref={fileInputRef}
                        type="file"
                        hidden
                        accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp"
                        onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                      />
                    </label>
                    {attachment ? <small>{attachment.name}</small> : null}
                    <button type="submit" className="sd-btn sd-btn--primary" disabled={sending}>
                      <LuSend aria-hidden /> {sending ? "Saving..." : "Add Note"}
                    </button>
                  </div>
                </form>
              ) : (
                <div className="sd-permission-notice" role="note">
                  <LuLock aria-hidden />
                  <p>Notes are read-only for this account.</p>
                </div>
              )}
            </div>
          </section>

          <section className="sd-card sd-activity-card">
            <header>
              <h2>Activity Timeline</h2>
              {activities.length > 3 ? (
                <button
                  type="button"
                  onClick={() => setShowAllTimeline((current) => !current)}
                  aria-expanded={showAllTimeline}
                >
                  {showAllTimeline ? "Show Less" : `View All (${activities.length})`}
                </button>
              ) : null}
            </header>
            <ol className="sd-timeline">
              {activities.length === 0 ? (
                <li className="sd-empty">No activity recorded yet.</li>
              ) : (
                visibleTimeline.map((item) => (
                  <li key={item.id}>
                    <div className="sd-timeline__dot" aria-hidden />
                    <div>
                      <strong>{item.title}</strong>
                      <p>{item.remarks || "—"}</p>
                      <small>
                        {item.performedByName || item.performedByType} ·{" "}
                        {formatDateTime(item.createdAt)}
                      </small>
                    </div>
                  </li>
                ))
              )}
            </ol>
          </section>
        </main>

        <aside className="sd-detail-side">
          <section className="sd-card sd-status-card">
            <h2>
              <LuUserRound aria-hidden /> Status Management
            </h2>
            {permissions.canUpdateStatus ? (
              <>
                <label className="sd-field">
                  <span>Current Status</span>
                  <select value={status} onChange={(e) => setStatus(e.target.value)}>
                    {statusChoices.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                {(status === "resolved" || status === "closed") && (
                  <label className="sd-field">
                    <span>Resolution / Closure Notes</span>
                    <textarea
                      value={resolutionNotes}
                      onChange={(e) => setResolutionNotes(e.target.value)}
                      rows={4}
                      placeholder="Summarize the resolution or closure reason"
                    />
                  </label>
                )}
                <button
                  type="button"
                  className="sd-btn sd-btn--primary sd-btn--block"
                  disabled={savingStatus}
                  onClick={onSaveStatus}
                >
                  {savingStatus ? "Saving..." : "Update Status"}
                </button>
              </>
            ) : (
              <div className="sd-permission-notice" role="note">
                <LuLock aria-hidden />
                <p>{permissions.statusRestrictionMessage}</p>
              </div>
            )}
            <ol className="sd-status-flow">
              {statusChoices.map((item) => (
                <li key={item.value} className={item.value === feedback.status ? "is-current" : ""}>
                  <i aria-hidden />
                  <span>{item.label}</span>
                  {item.value === feedback.status ? <small>Current status</small> : null}
                </li>
              ))}
            </ol>
          </section>

          <section className="sd-card sd-attachments-card">
            <h2>
              <LuPaperclip aria-hidden /> Attachments ({attachments.length})
            </h2>
            {attachments.length ? (
              <div className="sd-attachment-list">
                {attachments.map((item) => (
                  <a
                    key={item.id}
                    href={resolvePublicFileUrl(item.path)}
                    target="_blank"
                    rel="noreferrer"
                  >
                    <LuFileText aria-hidden />
                    <span>{item.name}</span>
                    <LuDownload aria-hidden />
                  </a>
                ))}
              </div>
            ) : (
              <p className="sd-card-empty">No attachments added.</p>
            )}
          </section>

          <section className="sd-card sd-quick-actions">
            <h2>Quick Actions</h2>
            {permissions.canAddInternalNotes ? (
              <button type="button" className="is-note" onClick={beginInternalNote}>
                <LuLock aria-hidden /> Add Internal Note
              </button>
            ) : null}
            {permissions.canAssign && String(feedback.status || "").toLowerCase() !== "closed" ? (
              <button type="button" className="is-info" onClick={openAssignModal}>
                <LuUserPlus aria-hidden />{" "}
                {feedback.assignmentStatus === "assigned" ? "Reassign Feedback" : "Assign Feedback"}
              </button>
            ) : null}
            {!permissions.canAddInternalNotes && !permissions.canAssign ? (
              <div className="sd-permission-notice" role="note">
                <LuLock aria-hidden />
                <p>No actions available in read-only mode.</p>
              </div>
            ) : null}
          </section>

          <section className="sd-card sd-help-card">
            <LuInfo aria-hidden />
            <div>
              <h2>Need Help?</h2>
              <p>Check our help center or contact your team lead for assistance.</p>
              <Link to="/help-center">Visit Help Center</Link>
            </div>
          </section>
        </aside>
      </div>

      {showAssignModal
        ? createPortal(
            <div
              className="sd-modal"
              role="dialog"
              aria-modal="true"
              aria-labelledby="sd-feedback-assign-title"
              onClick={() => setShowAssignModal(false)}
            >
              <div
                className="sd-modal__panel"
                style={{ maxWidth: 520 }}
                onClick={(event) => event.stopPropagation()}
              >
                <header className="sd-quick-view__head">
                  <div className="sd-quick-view__head-main">
                    <h2 id="sd-feedback-assign-title">
                      {feedback.assignmentStatus === "assigned"
                        ? "Reassign Feedback"
                        : "Assign Feedback"}
                    </h2>
                    <p className="sd-quick-view__subject">
                      {feedback.feedbackNumber} — {feedback.subject}
                    </p>
                  </div>
                  <button
                    type="button"
                    className="sd-icon-btn sd-icon-btn--ghost"
                    aria-label="Close"
                    onClick={() => setShowAssignModal(false)}
                  >
                    <LuX aria-hidden />
                  </button>
                </header>
                <div className="sd-quick-view__body">
                  <label className="sd-field">
                    <span>Support User</span>
                    <select
                      value={assigneeId}
                      onChange={(e) => setAssigneeId(e.target.value)}
                    >
                      <option value="">Select support user</option>
                      {assignees
                        .filter((user) => user.accountType !== "support_manager")
                        .map((user) => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                    </select>
                  </label>
                  <label className="sd-field">
                    <span>Reason (optional)</span>
                    <input
                      type="text"
                      value={assignReason}
                      onChange={(e) => setAssignReason(e.target.value)}
                      placeholder="Why assign this feedback?"
                    />
                  </label>
                  <label className="sd-field">
                    <span>Notes (optional)</span>
                    <textarea
                      value={assignNotes}
                      onChange={(e) => setAssignNotes(e.target.value)}
                      rows={3}
                      placeholder="Internal assignment notes"
                    />
                  </label>
                </div>
                <footer className="sd-quick-view__footer">
                  <button
                    type="button"
                    className="sd-btn sd-btn--ghost"
                    onClick={() => setShowAssignModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="sd-btn sd-btn--primary"
                    disabled={assigning || !assigneeId}
                    onClick={submitAssign}
                  >
                    {assigning ? "Assigning..." : "Assign"}
                  </button>
                </footer>
              </div>
            </div>,
            document.body,
          )
        : null}
    </div>
  );
}
